import React, { createContext, useEffect, useState } from 'react';
import PaymentVerificationStatus from 'domain/Transactions/Dashboard/Filter/PaymentVerificationStatus';
import useAuthorization from 'hooks/useAuthorization';
import useLog from 'hooks/useLog';
import useSwitchAccount from 'hooks/useSwitchAccount';
import { UserRole } from 'models/user';
import routes from 'routes.path';
import browserHistory from 'services/history/browserHistory';
import t from 'utils/translationHelper';

import { ActionButton } from '@alpha/ui-lib/ui/button';
import { Box, Typography } from '@alpha/ui-lib/ui/external';
import { StyledTabsWrapper } from '@alpha/ui-lib/ui/Tabs';

import useStyles from '../../index.styles';

import BeneficiaryApprovals from './BeneficiaryApprovals';
import PaymentApprovals from './PaymentApprovals';
import TradeApprovals from './TradeApprovals';
import TransferApprovals from './TransferApprovals';
import VerificationApprovals from './VerificationApprovals';

export type TOutstandingActionsContext = {
  pendingPaymentsCount?: number,
  setPendingPaymentsCount?: React.Dispatch<number>,
  pendingBenesCount?: number,
  setPendingBenesCount?: React.Dispatch<number>,
  pendingTransfersCount?: number,
  setPendingTransfersCount?: React.Dispatch<number>,
  pendingTradesCount?: number,
  setPendingTradesCount?: React.Dispatch<number>,
  pendingVerificationsCount?: number,
  setPendingVerificationsCount?: React.Dispatch<number>,
};

export const OutstandingActionsContext = createContext<TOutstandingActionsContext>({});

type TabType = 'payment' | 'beneficiary' | 'trade' | 'transfer' | 'verification';

interface TabConfig {
  type: TabType;
  title: string;
  component: React.ReactNode;
  shouldShow: boolean;
}

const OutstandingActionWidget: React.FC = () => {
  const classes = useStyles();

  const { logEvent } = useLog();
  const [pendingPaymentsCount, setPendingPaymentsCount] = useState(0);
  const [pendingBenesCount, setPendingBenesCount] = useState(0);
  const [pendingTradesCount, setPendingTradesCount] = useState(0);
  const [pendingTransfersCount, setPendingTransfersCount] = useState(0);
  const [pendingVerificationsCount, setPendingVerificationsCount] = useState(0);
  const { authorized: authorisedIATApprover } = useAuthorization([[UserRole.IAT_APPROVER]]);
  const { authorized: authorisedVOPApprover } = useAuthorization(
    [[UserRole.PAYMENTS_INPUTTER, UserRole.PAYMENTS_APPROVER, UserRole.PAYMENTS_APPROVER_OWN, UserRole.PAYMENT_FILE_INPUTTER]],
  );
  const { isEMoneyDisabled } = useSwitchAccount();

  const [tabIndex, setTabIndex] = useState(0);

  const contextValue = {
    pendingPaymentsCount,
    setPendingPaymentsCount,
    pendingBenesCount,
    setPendingBenesCount,
    pendingTradesCount,
    setPendingTradesCount,
    pendingTransfersCount,
    setPendingTransfersCount,
    pendingVerificationsCount,
    setPendingVerificationsCount,
  };

  const allTabs: TabConfig[] = [
    {
      type: 'payment',
      title: `${t('payment_approvals')} ${pendingPaymentsCount > 0 ? `(${pendingPaymentsCount})` : ''}`,
      component: <PaymentApprovals />,
      shouldShow: !isEMoneyDisabled(),
    },
    {
      type: 'beneficiary',
      title: `${t('beneficiary_approvals')} ${pendingBenesCount > 0 ? `(${pendingBenesCount})` : ''}`,
      component: <BeneficiaryApprovals />,
      shouldShow: true,
    },
    {
      type: 'trade',
      title: `${t('trade_approvals')} ${pendingTradesCount > 0 ? `(${pendingTradesCount})` : ''}`,
      component: <TradeApprovals />,
      shouldShow: !isEMoneyDisabled(),
    },
    {
      type: 'transfer',
      title: `${t('transfer_approvals')} ${pendingTransfersCount > 0 ? `(${pendingTransfersCount})` : ''}`,
      component: <TransferApprovals />,
      shouldShow: authorisedIATApprover && !isEMoneyDisabled(),
    },
    {
      type: 'verification',
      title: `${t('verification_approvals')} ${pendingVerificationsCount > 0 ? `(${pendingVerificationsCount})` : ''}`,
      component: <VerificationApprovals />,
      shouldShow: authorisedVOPApprover && !isEMoneyDisabled(),
    },
  ];

  const visibleTabs = allTabs.filter((tab) => tab.shouldShow);
  const tabTitles = visibleTabs.map((tab) => tab.title);

  useEffect(() => {
    logEvent({ action: 'Dashboard - View Outstanding Actions' });
  }, []);

  const handleViewAllButtonClick = () => {
    if (visibleTabs.length === 0) return;

    const currentTab = visibleTabs[tabIndex];
    let pageToGo = routes.dashboard;
    let widgetName = '';

    switch (currentTab.type) {
      case 'payment':
        pageToGo = `${routes.transactions.payments}?paymentStatus=SUBMITTED`;
        widgetName = 'Pending Payments';
        break;
      case 'beneficiary':
        pageToGo = `${routes.beneficiaries}?tab=pending`;
        widgetName = 'Pending Beneficiaries';
        break;
      case 'trade':
        pageToGo = `${routes.transactions.base}?tradeStatus=SUBMITTED`;
        widgetName = t('pending_trades');
        break;
      case 'transfer':
        pageToGo = `${routes.transactions.transfer}`;
        widgetName = 'Pending Transfers';
        break;
      case 'verification':
        pageToGo = `${routes.transactions.payments}?paymentVerificationStatus=${PaymentVerificationStatus.PENDING}`;
        widgetName = 'Pending Verifications';
        break;
      default:
        break;
    }

    logEvent({ action: 'Dashboard - View All', detail: { widget: widgetName } });
    browserHistory.push(pageToGo);
  };

  return (
    <Box
      className={classes.widget}
      style={{
        gridColumn: 'span 2',
      }}
    >
      <Box className={classes.headerContainer}>
        <Typography className={classes.title}>{t('outstanding_actions')}</Typography>
        {' '}
        <ActionButton
          style={{ background: '#F7F7F7', color: '#212529' }}
          onClick={handleViewAllButtonClick}
        >
          {t('view_all~')}
        </ActionButton>

      </Box>
      <StyledTabsWrapper
        className={classes.tradesCommingToValueES}
        testId="outstanding-actions-tabs-wrapper"
        tabTitles={tabTitles}
        tabIndex={tabIndex}
        handleChange={(selectedTabIndex: number) => {
          setTabIndex(selectedTabIndex);
          logEvent({
            action: 'Dashboard - Outstanding Actions Click Tab',
            detail: {
              tab: selectedTabIndex,
              tabName: tabTitles[selectedTabIndex],
            },
          });
        }}
      />
      <div style={{ marginTop: '4px' }}>
        <OutstandingActionsContext.Provider
          value={contextValue}
        >
          {visibleTabs.map((tab, index) => (
            <div key={tab.type} style={{ display: tabIndex === index ? 'block' : 'none' }}>
              {tab.component}
            </div>
          ))}
        </OutstandingActionsContext.Provider>
      </div>
    </Box>
  );
};

export default OutstandingActionWidget;
